import {useScanner} from '@/shared/integrations/scanner'
import {Avatar, Box, Button, Steps, Text, useSteps} from '@chakra-ui/react'
import {
  useFindLabelPrescriptions,
  useFindPatientsByHospitalId,
  useGetPatientTherapySequence,
} from '@/modules/patients-therapies/api'
import {labelMatchesPrescription, parseScanCode, type LabelScanCode} from '@/modules/patients-therapies/scan-code'
import {Patient, PatientTherapySequence} from '@cypro/engine/schema'
import {AdministrationMode} from '@cypro/engine/database/schema'
import {drawer} from '@cypro/ui/components'
import {useRef, useState} from 'react'
import {LuCircleCheckBig, LuOctagonAlert, LuSkipForward} from 'react-icons/lu'
import {useTherapySequenceActions} from '@/modules/patients-therapies/hooks'
import * as z from 'zod'
import {formatTreatmentLabels} from '../../functions'
import {useTranslate} from '@tolgee/react'
import {useGetPatient} from '@/modules/patients/api'
import {DrawerBody, DrawerFooter} from '@cypro/ui/components'
import {ScanPatientIcon} from '@/shared/components/ui/scan-patient-icon'
import {ScanMedicationIcon} from '@/shared/components/ui/scan-medication-icon'
import {isBlockedByPastTherapyDate, matchWristbandScan, type WristbandScanResult} from '@cypro/engine/utils'
import {usePastTherapyDateWarning} from '../past-therapy-date-alert'
import {usePreparationUseBy} from '@/modules/patients-therapies/hooks/use-preparation-use-by'
import {PreparationExpiredAlert, UseByValue} from '../preparation-use-by/preparation-use-by'

const hasPDA = true

const items = [
  {
    id: 1,
    content: StepOneContent,
  },
  {
    id: 2,
    content: StepTwoContent,
  },
  {
    id: 3,
    content: StepThreeContent,
  },
]

interface StepsContentProps {
  sequence: PatientTherapySequence
  scannedId?: string
  scannedPatient?: Patient
  scannedSequence?: PatientTherapySequence
  rescan?: () => void
  skipPatientScan?: () => void
  patientScanSkipped?: boolean
}

export function AdministerDrawer({sequence}: {sequence: PatientTherapySequence}) {
  const steps = useSteps({
    defaultStep: 0,
    count: 3,
  })

  const [scannedPatientId, setScannedPatientId] = useState('')
  const [scannedSequenceId, setScannedSequenceId] = useState('')
  const [patientScanSkipped, setPatientScanSkipped] = useState(false)
  const [patient] = useGetPatient(scannedPatientId)
  const [scannedSequence] = useGetPatientTherapySequence(scannedSequenceId)

  const findLabelPrescriptions = useFindLabelPrescriptions()

  // Preparation label scanned at the bag step: it must carry this prescription's number and patient.
  async function onLabelScanned(label: LabelScanCode) {
    if (labelMatchesPrescription(label, sequence)) {
      steps.goToNextStep()
      return
    }
    const [match] = await findLabelPrescriptions(label)
    setScanned(match?.id ?? 'unknown')
  }

  const findPatientsByHospitalId = useFindPatientsByHospitalId()
  // Bumped on every patient scan, rescan and skip, so a late wristband lookup never acts on a newer step.
  const patientScanRef = useRef(0)

  // Wristband scanned at the patient step: its hospital ID must be this patient's and no one else's (CY-326).
  async function onWristbandScanned(value: string) {
    const scan = patientScanRef.current
    let result: WristbandScanResult
    try {
      const candidates = await findPatientsByHospitalId(value)
      const patient = {id: sequence.patientId, hospitalPatientId: sequence.patient?.hospitalPatientId}
      result = matchWristbandScan(value, patient, candidates)
    } catch (e) {
      console.error(e)
      result = {matches: false, scannedPatientId: null}
    }
    if (scan !== patientScanRef.current) return
    if (result.matches) {
      steps.goToNextStep()
      return
    }
    setScannedPatientId(result.scannedPatientId ?? value)
  }

  const drawerRef = useRef(null)
  useScanner(drawerRef, {
    onComplete(value) {
      if (hasPDA) {
        try {
          const scanCode = parseScanCode(value)
          if (scanCode?.type === 'label' && steps.value === 1) {
            onLabelScanned(scanCode).catch(console.error)
            return
          }

          if (steps.value === 0) patientScanRef.current += 1
          // Anything but Cypro's own P:/S: codes at the patient step is read as a wristband hospital ID.
          if (steps.value === 0 && scanCode?.type !== 'patient' && scanCode?.type !== 'sequence') {
            onWristbandScanned(value).catch(console.error)
            return
          }

          const codes = value.split(':')
          if (codes.length !== 2) {
            setScanned(value)
            return
          }

          if (codes[1]) {
            const {success: isUuid} = z.uuid().safeParse(codes[1])
            if (!isUuid) {
              setScanned(value)
              return
            }
          }

          if (codes[0] !== 'S' && steps.value === 0) {
            if (sequence.patientId === codes[1]) {
              steps.goToNextStep()
            } else {
              setScanned(codes[1])
            }
            return
          }
          if (codes[0] === 'S' && steps.value === 1) {
            if (sequence.id === codes[1]) {
              steps.goToNextStep()
            } else {
              setScanned(codes[1])
            }
            return
          }

          setScanned('unknown')
        } catch (e) {
          console.error(e)
        }
      }
    },
    global: true,
  })

  function setScanned(code: string) {
    if (steps.value === 0) {
      setScannedPatientId(code)
    } else {
      setScannedSequenceId(code)
    }
  }

  // Patient without a usable wristband (or a wrong one): go on to the label scan, which still
  // identifies the patient; the skip is saved with the administration (CY-212).
  function skipPatientScan() {
    patientScanRef.current += 1
    setScannedPatientId('')
    setPatientScanSkipped(true)
    steps.goToNextStep()
  }

  function rescan() {
    if (steps.value === 0) {
      patientScanRef.current += 1
      setScannedPatientId('')
    }

    if (steps.value === 1) {
      setScannedSequenceId('')
    }
  }

  return (
    <Steps.RootProvider value={steps} size="sm" colorPalette={'cyan'}>
      <Box
        ref={drawerRef}
        width="100%"
        display="flex"
        flexDir="column"
        justifyContent="space-between"
        paddingBlock={2}
        paddingInline={4}
        gap={4}
      >
        {items.map((step, index) => (
          <Steps.Content key={step.id} index={index}>
            {
              <step.content
                sequence={sequence}
                scannedId={step.id === 1 ? scannedPatientId : scannedSequenceId}
                rescan={rescan}
                skipPatientScan={step.id === 1 ? skipPatientScan : undefined}
                patientScanSkipped={patientScanSkipped}
                scannedPatient={patient}
                scannedSequence={scannedSequence}
              />
            }
          </Steps.Content>
        ))}
      </Box>
    </Steps.RootProvider>
  )
}
function StepOneContent({sequence, scannedId, rescan, skipPatientScan, scannedPatient}: StepsContentProps) {
  const {t} = useTranslate('common')
  const isMismatch = sequence.patientId !== scannedId && scannedId && scannedId.length > 0
  return (
    <>
      <DrawerBody>
        <Box display="flex" flexDir="column" gap={2} alignItems="center" paddingBlock={4}>
          <Box width="100%" display="flex" flexDir="column" gap={2} alignItems="center" paddingBlock={2}>
            <Avatar.Root
              variant={isMismatch ? 'solid' : 'outline'}
              colorPalette={isMismatch ? 'red' : 'gray'}
              width="64px"
              height="64px"
              borderWidth={isMismatch ? '1px' : '0px'}
              shadow={isMismatch ? 'md' : 'none'}
            >
              {isMismatch ? <LuOctagonAlert size="32" /> : <ScanPatientIcon />}
            </Avatar.Root>

            <Text fontSize="lg" fontWeight="semibold">
              {isMismatch ? t('MISMATCH_ERROR') : t('patient_verification')}
            </Text>
            <Text fontSize="md" fontWeight="medium" textAlign={'center'}>
              {isMismatch
                ? t('barcode_not_matched_patient')
                : t('scan_patient_barcode', {name: sequence?.patient?.fullName})}
            </Text>
            {isMismatch && (
              <Box display="flex" flexDir="column" width="100%" gap="3">
                <Box
                  display="flex"
                  flexDir="row"
                  justifyContent="space-between"
                  borderBottom="0.5px solid"
                  borderColor="border"
                >
                  <Text fontSize="xs" color="fg.muted">
                    {t('scanned_patient')}
                  </Text>
                  <Text fontSize="xs" color="fg.muted" fontWeight="medium">
                    {scannedPatient?.fullName || t('unknown')}
                  </Text>
                </Box>
                <Box display="flex" flexDir="row" justifyContent="space-between">
                  <Text fontSize="xs" color="fg.muted">
                    {t('required_patient')}
                  </Text>
                  <Text fontSize="xs" color="fg.muted" fontWeight="medium">
                    {sequence?.patient?.fullName}
                  </Text>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </DrawerBody>
      <DrawerFooter>
        <Box width="100%" display="flex" flexDir="column" gap={4} alignItems="center">
          <StepsList isMismatch={isMismatch} />
          {isMismatch ? (
            <Button width="100%" colorPalette={'red'} onClick={rescan}>
              {t('rescan')}
            </Button>
          ) : (
            <Button width="100%" colorPalette={'cyan'} disabled>
              {t('scan_patient')}
            </Button>
          )}
          {/* Skip replaces "Continue anyway"; only the patient scan can be skipped, never the label scan. */}
          <Button width="100%" variant="outline" colorPalette={'gray'} onClick={skipPatientScan}>
            {t('skip_patient_scan', 'Skip patient scan')}
          </Button>
        </Box>
      </DrawerFooter>
    </>
  )
}

function StepsList({isMismatch, patientScanSkipped}: {isMismatch?: boolean | string; patientScanSkipped?: boolean}) {
  const {t} = useTranslate('common')
  return (
    <Steps.List width={'100%'} colorPalette={isMismatch ? 'red' : 'cyan'}>
      {items.map((step, index) => {
        // a skipped patient scan is not a successful one, so it doesn't get the completed tick
        const skipped = index === 0 && patientScanSkipped
        return (
          <Steps.Item key={step.id} index={index} title={step.id.toString()}>
            {skipped ? (
              <Steps.Indicator colorPalette="orange" aria-label={t('patient_scan_skipped', 'Patient scan skipped')}>
                <LuSkipForward />
              </Steps.Indicator>
            ) : (
              <Steps.Indicator />
            )}
            <Steps.Separator />
          </Steps.Item>
        )
      })}
    </Steps.List>
  )
}

function StepTwoContent({sequence, scannedId, scannedSequence, rescan, patientScanSkipped}: StepsContentProps) {
  const {t} = useTranslate('common')
  const isMismatch = sequence.id !== scannedId && scannedId && scannedId.length > 0
  const {names, dosages} = formatTreatmentLabels(sequence, t)
  const {names: scannedNames, dosages: scannedDosages} = formatTreatmentLabels(scannedSequence, t)
  return (
    <>
      <DrawerBody>
        <Box display="flex" flexDir="column" gap={2} alignItems="center" paddingBlock={4}>
          <Box width="100%" display="flex" flexDir="column" gap={2} alignItems="center" paddingBlock={2}>
            <Avatar.Root
              variant={isMismatch ? 'solid' : 'outline'}
              colorPalette={isMismatch ? 'red' : 'gray'}
              width="64px"
              height="64px"
              borderWidth={isMismatch ? '1px' : '0px'}
              shadow={isMismatch ? 'md' : 'none'}
            >
              {isMismatch ? <LuOctagonAlert size="32" /> : <ScanMedicationIcon />}
            </Avatar.Root>
            <Text fontSize="lg" fontWeight="semibold">
              {isMismatch ? t('MISMATCH_ERROR') : t('material_verification')}
            </Text>
            <Text fontSize="md" fontWeight="medium" textAlign={'center'}>
              {isMismatch
                ? t('barcode_not_matched_medication')
                : t('scan_medication_barcode', {medication: `${names.join(' / ')} ${dosages}`})}
            </Text>
            {isMismatch && (
              <Box display="flex" flexDir="column" width="100%" gap="3">
                <Box
                  display="flex"
                  flexDir="row"
                  justifyContent="space-between"
                  borderBottom="0.5px solid"
                  borderColor="border"
                >
                  <Text fontSize="xs" color="fg.muted">
                    {t('scanned_medication')}
                  </Text>
                  <Text fontSize="xs" color="fg.muted" fontWeight="medium">
                    {scannedNames.length > 0 ? `${scannedNames.join(' / ')} ${scannedDosages}` : 'Unknown'}
                  </Text>
                </Box>
                <Box display="flex" flexDir="row" justifyContent="space-between">
                  <Text fontSize="xs" color="fg.muted">
                    {t('required_medication')}
                  </Text>
                  <Text fontSize="xs" color="fg.muted" fontWeight="medium">
                    {names.join(' / ')} {dosages}
                  </Text>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </DrawerBody>
      <DrawerFooter>
        <Box width="100%" display="flex" flexDir="column" gap={6} alignItems="center">
          <Box width="100%" display="flex" flexDir="column" gap={2} alignItems="center">
            <StepsList isMismatch={isMismatch} patientScanSkipped={patientScanSkipped} />
            {patientScanSkipped && (
              <Text fontSize="xs" color="orange.fg" fontWeight="medium">
                {t('patient_scan_skipped', 'Patient scan skipped')}
              </Text>
            )}
          </Box>
          {isMismatch ? (
            <Button width="100%" colorPalette={'red'} onClick={rescan}>
              {t('rescan')}
            </Button>
          ) : (
            <Button width="100%" colorPalette={'cyan'} disabled>
              {t('scan_medication')}
            </Button>
          )}
        </Box>
      </DrawerFooter>
    </>
  )
}
function StepThreeContent({sequence, patientScanSkipped}: StepsContentProps) {
  const {t} = useTranslate('common')
  const {sendEvent} = useTherapySequenceActions(sequence)
  const {names, dosages} = formatTreatmentLabels(sequence, t)
  const isSequenceInThePast = isBlockedByPastTherapyDate(sequence)
  const pastDateWarning = usePastTherapyDateWarning(sequence)
  // an expired bag is never administered (CY-327)
  const {useBy, isExpired} = usePreparationUseBy(sequence.id)
  return (
    <>
      <DrawerBody>
        <Box display="flex" flexDir="column" gap={2} alignItems="center" paddingBlock={4}>
          <Box width="100%" display="flex" flexDir="column" gap={2} alignItems="center" paddingBlock={2}>
            <Avatar.Root colorPalette={'green'} width="64px" height="64px" borderWidth={'1px'} shadow={'md'}>
              <LuCircleCheckBig size="32" />
            </Avatar.Root>
            <Text fontSize="lg" fontWeight="semibold">
              {t('ready_to_administer')}
            </Text>
            <Box display="flex" flexDir="column" width="100%" gap="3">
              <Box
                display="flex"
                flexDir="row"
                justifyContent="space-between"
                borderBottom="0.5px solid"
                borderColor="border"
              >
                <Text fontSize="xs" color="fg.muted">
                  {t('event.domain.patient')}
                </Text>
                <Text fontSize="xs" color="fg.muted" fontWeight="medium">
                  {sequence?.patient?.fullName}
                </Text>
              </Box>
              <Box
                display="flex"
                flexDir="row"
                justifyContent="space-between"
                borderBottom="0.5px solid"
                borderColor="border"
              >
                <Text fontSize="xs" color="fg.muted">
                  {t('medication')}
                </Text>
                <Text fontSize="xs" color="fg.muted" fontWeight="medium">
                  {names.join(' / ')} {dosages}
                </Text>
              </Box>
              {patientScanSkipped && (
                <Box
                  display="flex"
                  flexDir="row"
                  justifyContent="space-between"
                  borderBottom="0.5px solid"
                  borderColor="border"
                >
                  <Text fontSize="xs" color="fg.muted">
                    {t('patient_verification')}
                  </Text>
                  <Text fontSize="xs" color="orange.fg" fontWeight="medium">
                    {t('patient_scan_skipped', 'Patient scan skipped')}
                  </Text>
                </Box>
              )}
              <Box
                display="flex"
                flexDir="row"
                justifyContent="space-between"
                borderBottom="0.5px solid"
                borderColor="border"
              >
                <Text fontSize="xs" color="fg.muted">
                  {t('use_by')}
                </Text>
                <Text fontSize="xs" color="fg.muted" fontWeight="medium">
                  <UseByValue useBy={useBy} />
                </Text>
              </Box>
              <Box display="flex" flexDir="row" justifyContent="space-between">
                <Text fontSize="xs" color="fg.muted">
                  {t('time_check')}
                </Text>
                <Text fontSize="xs" color={pastDateWarning ? 'orange.fg' : 'fg.muted'} fontWeight="medium">
                  {pastDateWarning ?? t('OK')}
                </Text>
              </Box>
            </Box>
            {isExpired && <PreparationExpiredAlert />}
          </Box>
        </Box>
      </DrawerBody>
      <DrawerFooter>
        <Box width="100%" display="flex" flexDir="column" gap={6} alignItems="center">
          <StepsList patientScanSkipped={patientScanSkipped} />

          <Button
            width="100%"
            colorPalette={'cyan'}
            disabled={isSequenceInThePast || isExpired}
            onClick={() => {
              sendEvent({
                id: sequence.id,
                event: {
                  type: 'ADMINISTER',
                  body: {
                    completed: true,
                    mode: AdministrationMode.enum.SCAN,
                    patientScanSkipped: Boolean(patientScanSkipped),
                  },
                },
              })
              drawer.close(`administed-drawer-${sequence.id}`)
            }}
          >
            {t('mark_as_administer')}
          </Button>
        </Box>
      </DrawerFooter>
    </>
  )
}
